import { collection, doc, getDoc, getDocs, query, where, setDoc, updateDoc, deleteDoc, orderBy, limit, Timestamp, serverTimestamp, writeBatch } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../config/firebase';
import { GlossaryTerm, GlossarySyncStatus, GlossaryExtension, GlossaryFavorite, GlossaryCategory, TermOfTheDay, GlossaryMetrics } from '../types/glossary';

/**
 * Service for interacting with glossary data in Firestore
 */
class GlossaryService {
  // Collection names
  private GLOSSARY_COLLECTION = 'glossary';
  private SYNC_STATUS_COLLECTION = 'glossarySync';
  private EXTENSION_COLLECTION = 'glossaryExtension';
  private FAVORITES_COLLECTION = 'glossaryFavorites';
  private CATEGORIES_COLLECTION = 'glossaryCategories';
  private TERM_OF_DAY_COLLECTION = 'glossaryTermOfDay';

  /**
   * Get all glossary terms
   * @returns Promise that resolves to an array of glossary terms
   */
  async getGlossaryTerms(): Promise<GlossaryTerm[]> {
    try {
      const termsSnapshot = await getDocs(collection(db, this.GLOSSARY_COLLECTION));
      return termsSnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          term: data.term,
          content: data.content,
          timestamp: data.timestamp,
          categoryId: data.categoryId,
          categoryName: data.categoryName
        } as GlossaryTerm;
      });
    } catch (error) {
      console.error('Error fetching glossary terms:', error);
      throw error;
    }
  }

  /**
   * Get a glossary term by ID
   * @param id The ID of the term to fetch
   * @returns Promise that resolves to the glossary term or null if not found
   */
  async getGlossaryTermById(id: string): Promise<GlossaryTerm | null> {
    try {
      const termDoc = await getDoc(doc(db, this.GLOSSARY_COLLECTION, id));
      
      if (!termDoc.exists()) {
        return null;
      }
      
      const data = termDoc.data();
      return {
        id: termDoc.id,
        term: data.term,
        content: data.content,
        timestamp: data.timestamp,
        categoryId: data.categoryId,
        categoryName: data.categoryName
      } as GlossaryTerm;
    } catch (error) {
      console.error(`Error fetching glossary term ${id}:`, error);
      throw error;
    }
  }

  /**
   * Get the term of the day
   * @returns Promise that resolves to the term of the day
   */
  async getTermOfTheDay(): Promise<GlossaryTerm | null> {
    try {
      // Get the current date in YYYY-MM-DD format
      const today = new Date().toISOString().split('T')[0];
      
      // Check if we already have a term for today
      const termOfDayRef = doc(db, this.TERM_OF_DAY_COLLECTION, 'current');
      const termOfDayDoc = await getDoc(termOfDayRef);
      
      if (termOfDayDoc.exists() && termOfDayDoc.data()?.date === today) {
        // We already have a term for today, return it
        const termId = termOfDayDoc.data()?.termId;
        console.log('Using existing term of the day:', termId);
        const term = await this.getGlossaryTermById(termId);
        if (!term) {
          console.log('Existing term not found, selecting new one');
          // Term doesn't exist anymore, select a new one
          return this.selectNewTermOfTheDay(today, termOfDayRef);
        }
        return term;
      } else {
        // Select a new term for today
        console.log('Selecting new term of the day for:', today);
        return this.selectNewTermOfTheDay(today, termOfDayRef);
      }
    } catch (error) {
      console.error('Error getting term of the day:', error);
      throw error;
    }
  }

  private async selectNewTermOfTheDay(today: string, termOfDayRef: any): Promise<GlossaryTerm | null> {
    const terms = await this.getGlossaryTerms();
    console.log('Available terms for term of the day:', terms.length);
    
    if (terms.length === 0) {
      console.log('No terms available for term of the day');
      return null;
    }
    
    // Use the date to deterministically select a term
    // This ensures all users see the same term on the same day
    const dateHash = this.hashString(today);
    const selectedIndex = dateHash % terms.length;
    const selectedTerm = terms[selectedIndex];
    
    console.log('Selected term of the day:', selectedTerm.term, 'ID:', selectedTerm.id);
    
    // Save this term as the term of the day
    await setDoc(termOfDayRef, {
      date: today,
      termId: selectedTerm.id
    });
    
    return selectedTerm;
  }

  /**
   * Simple string hash function
   * @param str String to hash
   * @returns A number hash of the string
   */
  private hashString(str: string): number {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32bit integer
    }
    return Math.abs(hash);
  }

  /**
   * Get the latest sync status
   * @returns Promise that resolves to the latest sync status
   */
  async getGlossarySyncStatus(): Promise<GlossarySyncStatus | null> {
    try {
      const syncDoc = await getDoc(doc(db, this.SYNC_STATUS_COLLECTION, 'latest'));
      
      if (!syncDoc.exists()) {
        return null;
      }
      
      return syncDoc.data() as GlossarySyncStatus;
    } catch (error) {
      console.error('Error fetching glossary sync status:', error);
      throw error;
    }
  }

  /**
   * Get metrics for the glossary app card
   * @returns Promise that resolves to the glossary metrics
   */
  async getGlossaryMetrics(): Promise<GlossaryMetrics> {
    try {
      // Get total number of terms
      const termsSnapshot = await getDocs(collection(db, this.GLOSSARY_COLLECTION));
      const totalTerms = termsSnapshot.size;
      
      // Get last sync date
      const syncStatus = await this.getGlossarySyncStatus();
      const lastSyncDate = syncStatus?.lastSync 
        ? new Date(syncStatus.lastSync.toMillis()).toLocaleDateString()
        : 'Never';
      
      return {
        totalTerms,
        lastSyncDate
      };
    } catch (error) {
      console.error('Error fetching glossary metrics:', error);
      throw error;
    }
  }

  /**
   * Add a term to favorites
   * @param termId The ID of the term to add to favorites
   * @param userId The ID of the user
   * @param categoryId The ID of the category to add the term to
   * @returns Promise that resolves when the term is added to favorites
   */
  async addToFavorites(termId: string, userId: string, categoryId: string): Promise<void> {
    try {
      // Check if the term exists
      const termDoc = await getDoc(doc(db, this.GLOSSARY_COLLECTION, termId));
      if (!termDoc.exists()) {
        throw new Error(`Term ${termId} does not exist`);
      }
      
      // Check if the category exists
      const categoryDoc = await getDoc(doc(db, this.CATEGORIES_COLLECTION, categoryId));
      if (!categoryDoc.exists()) {
        throw new Error(`Category ${categoryId} does not exist`);
      }
      
      // Check if the category belongs to the user
      const categoryData = categoryDoc.data();
      if (categoryData.userId !== userId) {
        throw new Error(`Category ${categoryId} does not belong to user ${userId}`);
      }
      
      // Add the term to the category
      const termIds = categoryData.termIds || [];
      if (!termIds.includes(termId)) {
        termIds.push(termId);
        await updateDoc(doc(db, this.CATEGORIES_COLLECTION, categoryId), {
          termIds
        });
      }
      
      // Add a record to the favorites collection
      const favoriteId = `${userId}_${termId}`;
      await setDoc(doc(db, this.FAVORITES_COLLECTION, favoriteId), {
        userId,
        termId,
        categoryId,
        addedAt: serverTimestamp()
      });
    } catch (error) {
      console.error(`Error adding term ${termId} to favorites:`, error);
      throw error;
    }
  }

  /**
   * Remove a term from favorites
   * @param termId The ID of the term to remove from favorites
   * @param userId The ID of the user
   * @returns Promise that resolves when the term is removed from favorites
   */
  async removeFavorite(termId: string, userId: string): Promise<void> {
    try {
      // Get all categories for the user
      const categoriesSnapshot = await getDocs(
        query(collection(db, this.CATEGORIES_COLLECTION), where('userId', '==', userId))
      );
      
      // Remove the term from all categories
      const batch = writeBatch(db);
      
      categoriesSnapshot.forEach(categoryDoc => {
        const categoryData = categoryDoc.data();
        const termIds = categoryData.termIds || [];
        
        if (termIds.includes(termId)) {
          const updatedTermIds = termIds.filter((id: string) => id !== termId);
          batch.update(categoryDoc.ref, { termIds: updatedTermIds });
        }
      });
      
      // Remove the favorite record
      const favoriteId = `${userId}_${termId}`;
      batch.delete(doc(db, this.FAVORITES_COLLECTION, favoriteId));
      
      await batch.commit();
    } catch (error) {
      console.error(`Error removing term ${termId} from favorites:`, error);
      throw error;
    }
  }

  /**
   * Get all favorite terms for a user
   * @param userId The ID of the user
   * @returns Promise that resolves to an array of favorite terms
   */
  async getFavorites(userId: string): Promise<GlossaryFavorite[]> {
    try {
      const favoritesSnapshot = await getDocs(
        query(collection(db, this.FAVORITES_COLLECTION), where('userId', '==', userId))
      );
      
      return favoritesSnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          userId: data.userId,
          termId: data.termId,
          categoryId: data.categoryId,
          addedAt: data.addedAt
        } as GlossaryFavorite;
      });
    } catch (error) {
      console.error(`Error fetching favorites for user ${userId}:`, error);
      throw error;
    }
  }

  /**
   * Create a new category
   * @param name The name of the category
   * @param userId The ID of the user
   * @param createdBy The display name of the creator
   * @param creatorPhotoUrl The profile photo URL of the creator
   * @returns Promise that resolves to the ID of the new category
   */
  async createCategory(
    name: string,
    userId: string,
    createdBy: string,
    creatorPhotoUrl?: string
  ): Promise<string> {
    try {
      const categoryRef = doc(collection(db, this.CATEGORIES_COLLECTION));
      
      await setDoc(categoryRef, {
        name,
        userId,
        termIds: [],
        isPublic: false,
        createdAt: serverTimestamp(),
        createdBy,
        creatorPhotoUrl
      });
      
      return categoryRef.id;
    } catch (error) {
      console.error('Error creating category:', error);
      throw error;
    }
  }

  /**
   * Get all categories for a user
   * @param userId The ID of the user
   * @returns Promise that resolves to an array of categories
   */
  async getCategories(userId: string): Promise<GlossaryCategory[]> {
    try {
      const categoriesSnapshot = await getDocs(
        query(collection(db, this.CATEGORIES_COLLECTION), where('userId', '==', userId))
      );
      
      return categoriesSnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          name: data.name,
          userId: data.userId,
          termIds: data.termIds || [],
          isPublic: data.isPublic || false,
          createdAt: data.createdAt,
          createdBy: data.createdBy,
          creatorPhotoUrl: data.creatorPhotoUrl
        } as GlossaryCategory;
      });
    } catch (error) {
      console.error(`Error fetching categories for user ${userId}:`, error);
      throw error;
    }
  }

  /**
   * Update a category
   * @param categoryId The ID of the category to update
   * @param updates The updates to apply
   * @returns Promise that resolves when the category is updated
   */
  async updateCategory(
    categoryId: string,
    updates: Partial<Omit<GlossaryCategory, 'id' | 'userId' | 'createdAt' | 'createdBy' | 'creatorPhotoUrl'>>
  ): Promise<void> {
    try {
      await updateDoc(doc(db, this.CATEGORIES_COLLECTION, categoryId), updates);
    } catch (error) {
      console.error(`Error updating category ${categoryId}:`, error);
      throw error;
    }
  }

  /**
   * Delete a category
   * @param categoryId The ID of the category to delete
   * @returns Promise that resolves when the category is deleted
   */
  async deleteCategory(categoryId: string): Promise<void> {
    try {
      // Get the category to check if it's the default category
      const categoryDoc = await getDoc(doc(db, this.CATEGORIES_COLLECTION, categoryId));
      
      if (!categoryDoc.exists()) {
        throw new Error(`Category ${categoryId} does not exist`);
      }
      
      const categoryData = categoryDoc.data();
      
      // Don't allow deleting the default "My Favorites" category
      if (categoryData.name === 'My Favorites') {
        throw new Error('Cannot delete the default "My Favorites" category');
      }
      
      await deleteDoc(doc(db, this.CATEGORIES_COLLECTION, categoryId));
    } catch (error) {
      console.error(`Error deleting category ${categoryId}:`, error);
      throw error;
    }
  }

  /**
   * Upload a new extension version
   * @param file The extension file to upload
   * @param version The version number
   * @param notes Release notes
   * @returns Promise that resolves when the extension is uploaded
   */
  async uploadExtension(file: File, version: string, notes: string): Promise<void> {
    try {
      // Validate file is a .crx
      if (!file.name.endsWith('.crx')) {
        throw new Error('File must be a .crx file');
      }
      
      // Upload to Firebase Storage
      const storageRef = ref(storage, `extensions/glossary/${file.name}`);
      await uploadBytes(storageRef, file);
      const downloadUrl = await getDownloadURL(storageRef);
      
      // Save metadata to Firestore
      const extensionRef = doc(collection(db, this.EXTENSION_COLLECTION));
      await setDoc(extensionRef, {
        version,
        notes,
        filename: file.name,
        downloadUrl,
        uploadDate: serverTimestamp(),
        isLatest: true
      });
      
      // Update previous versions to not be latest
      const batch = writeBatch(db);
      const previousVersionsQuery = query(
        collection(db, this.EXTENSION_COLLECTION),
        where('isLatest', '==', true)
      );
      
      const previousVersions = await getDocs(previousVersionsQuery);
      previousVersions.forEach(doc => {
        if (doc.id !== extensionRef.id) {
          batch.update(doc.ref, { isLatest: false });
        }
      });
      
      await batch.commit();
    } catch (error) {
      console.error('Error uploading extension:', error);
      throw error;
    }
  }

  /**
   * Get the latest extension version
   * @returns Promise that resolves to the latest extension version
   */
  async getLatestExtension(): Promise<GlossaryExtension | null> {
    try {
      const extensionsSnapshot = await getDocs(
        query(
          collection(db, this.EXTENSION_COLLECTION),
          where('isLatest', '==', true),
          limit(1)
        )
      );
      
      if (extensionsSnapshot.empty) {
        return null;
      }
      
      const doc = extensionsSnapshot.docs[0];
      const data = doc.data();
      
      return {
        id: doc.id,
        version: data.version,
        notes: data.notes,
        filename: data.filename,
        downloadUrl: data.downloadUrl,
        uploadDate: data.uploadDate,
        isLatest: data.isLatest
      } as GlossaryExtension;
    } catch (error) {
      console.error('Error fetching latest extension:', error);
      throw error;
    }
  }

  /**
   * Get all extension versions
   * @returns Promise that resolves to an array of extension versions
   */
  async getExtensionHistory(): Promise<GlossaryExtension[]> {
    try {
      const extensionsSnapshot = await getDocs(
        query(
          collection(db, this.EXTENSION_COLLECTION),
          orderBy('uploadDate', 'desc')
        )
      );
      
      return extensionsSnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          version: data.version,
          notes: data.notes,
          filename: data.filename,
          downloadUrl: data.downloadUrl,
          uploadDate: data.uploadDate,
          isLatest: data.isLatest
        } as GlossaryExtension;
      });
    } catch (error) {
      console.error('Error fetching extension history:', error);
      throw error;
    }
  }

  /**
   * Check if a term is favorited by a user
   * @param termId The ID of the term to check
   * @param userId The ID of the user
   * @returns Promise that resolves to true if the term is favorited, false otherwise
   */
  async isTermFavorited(termId: string, userId: string): Promise<boolean> {
    try {
      const favoriteId = `${userId}_${termId}`;
      const favoriteDoc = await getDoc(doc(db, this.FAVORITES_COLLECTION, favoriteId));
      return favoriteDoc.exists();
    } catch (error) {
      console.error(`Error checking if term ${termId} is favorited by user ${userId}:`, error);
      throw error;
    }
  }
}

// Export a singleton instance
export const glossaryService = new GlossaryService();